import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import { Service } from '@/api/apiServices';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { Switch } from '@/components/ui/switch';
import { useSettings } from '@/hook/useSettings';
import { useServices } from '@/hooks/useServices';
import { ServiceAvatarDropdown } from './ServiceAvatarDropdown';
import { type ServiceFormValues, serviceFormSchema } from './service-schema';

interface ServiceDialogProps {
  open: boolean;
  onClose: () => void;
  service?: Service;
}

export function ServiceDialog({ open, onClose, service }: ServiceDialogProps) {
  const { createEditService, isCreatingOrEditing } = useServices();
  const { settingsData } = useSettings();

  const defaultValues = useMemo(() => ({
    serTitle: '',
    serDescription: '',
    serCredit: 0,
    serSupply: 0,
    serStatus: false,
    serAmount: 0,
    serNote: '',
    serCategory: [] as string[],
    serSpecialCategory: [] as string[],
    serAvatar: settingsData?.serviceAvatar?.[0] ?? {
      shape: 'halfTriangle',
      color1: '#c6f9eb',
      color2: '#ffffff',
    },
    serStation: '',
  }), [settingsData]);

  const form = useForm<ServiceFormValues>({
    resolver: zodResolver(serviceFormSchema),
    defaultValues: service
      ? {
          ...defaultValues,
          ...Object.fromEntries(Object.entries(service).filter(([key]) => key !== 'id')),
        }
      : defaultValues,
  });

  useEffect(() => {
    if (service) {
      form.reset(service);
    } else {
      form.reset(defaultValues);
    }
  }, [service, form, defaultValues, settingsData]);

  const handleClose = () => {
    form.reset(defaultValues);
    onClose();
  };

  const onSubmit = async (data: ServiceFormValues) => {
    try {
      // Create an object with only the fields that have values
      const serviceData: Partial<Service> = {};

      if (data.serTitle) serviceData.serTitle = data.serTitle;
      if (data.serDescription) serviceData.serDescription = data.serDescription;
      if (typeof data.serCredit === 'number') serviceData.serCredit = data.serCredit;
      if (typeof data.serSupply === 'number') serviceData.serSupply = data.serSupply;
      if (typeof data.serStatus === 'boolean') serviceData.serStatus = data.serStatus;
      if (typeof data.serAmount === 'number') serviceData.serAmount = data.serAmount;
      if (Array.isArray(data.serSpecialCategory))
        serviceData.serSpecialCategory = data.serSpecialCategory;
      if (Array.isArray(data.serCategory)) serviceData.serCategory = data.serCategory;
      if (data.serAvatar && typeof data.serAvatar === 'object')
        serviceData.serAvatar = data.serAvatar;

      // Include note and station with default empty string if not provided
      serviceData.serStation = data.serStation || '';
      serviceData.serNote = data.serNote || '';

      await createEditService({
        service: serviceData,
        id: service?.id,
      });

      onClose();
      form.reset();
    } catch (error) {
      console.error('Service error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save service');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-xl max-w-screen md:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{service ? 'Edit Service' : 'Create Service'}</DialogTitle>
          <DialogDescription>
            {/* {service ? 'Edit Service Form' : 'Create New Service Form'} */}
            {'Please fill out all required fields'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="serTitle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serCredit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credit</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="any"
                        {...field}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serSupply"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Supply</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serCategory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl>
                      <MultiSelect
                        selected={field.value}
                        options={settingsData?.serviceCategory ?? []}
                        onChange={field.onChange}
                        placeholder="Select categories"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serSpecialCategory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Special Category</FormLabel>
                    <FormControl>
                      <MultiSelect
                        selected={field.value}
                        options={settingsData?.specialCategory ?? []}
                        onChange={field.onChange}
                        placeholder="Select special categories"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serAvatar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Avatar</FormLabel>
                    <FormControl>
                      <ServiceAvatarDropdown
                        value={
                          field.value || { shape: 'square', color1: '#000000', color2: '#ffffff' }
                        }
                        onChange={field.onChange}
                        rowNumber={service?.id ? Number(service.id) : undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serNote"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Note</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serStatus"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-row items-center justify-between">
                      <FormLabel>Active Status</FormLabel>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreatingOrEditing}>
                {isCreatingOrEditing ? 'Saving...' : service ? 'Save' : 'Create'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
